import { XeroTableDataFetch, PayrollTableDataFetch } from "components";
import {
  ViewingRecords,
  Pagination,
  TableContainer,
  BulkDeleteButton,
  BulkUndeleteButton,
  TableSearch,
} from "components/common";
import { compose, withStateHandlers, withProps } from "recompose";
import { reduce, keyBy, keys, isFunction } from "lodash";
import shortid from "shortid";
import React from "react";
import { PayrollClient } from "services";
import { Trans } from 'react-i18next';

const callFunction = (fn, props) => (isFunction(fn) ? fn(props) : fn);

export default ({
  limit,
  offset,
  selected,
  filters: defaultFilters,
  url: defaultUrl,
  ordering,
  data,
  idKey = "id",
  search,
  singular: defaultSingular,
  plural: defaultPlural,
} = {}) =>
  compose(
    withProps((props) => ({
      plural: props.plural || defaultPlural,
      singular: props.singular || defaultSingular,
      url: props.url || defaultUrl,
    })),
    withStateHandlers(
      (props) => ({
        data: data || props.data || { count: 0, results: [] },
        offset: offset || props.offset || 0,
        limit: limit || props.limit || 50,
        filters:
          callFunction(defaultFilters, props) ||
          callFunction(props.filters, props) ||
          {},
        numSelected: 0,
        search: search || props.search || null,
        selected: selected || props.selected || {},
        ordering: ordering || props.ordering || "",
        isDeleteModalOpen: false,
        refreshToken: null,
      }),
      {
        setData:
          ({ selected, offset }) =>
          (data) => {
            const results = keyBy(data.results, idKey);

            selected = reduce(
              selected,
              (acc, value, key) => {
                if (results[key]) acc[key] = true;
                return acc;
              },
              {}
            );

            return { data, selected, offset };
          },
        updateResults:
          ({ data }) =>
          (arg) => {
            let results = isFunction(arg) ? arg(data.results) : arg;

            return {
              data: {
                ...data,
                results,
              },
            };
          },
        setOffset: () => (offset) => ({ offset }),
        setSelected: () => (selected) => ({
          selected,
          numSelected: keys(selected).length,
        }),
        setOrdering: () => (ordering) => ({ ordering }),
        setLimit: () => (limit) => ({ limit }),
        setSearch: () => (search) => ({ search}),
        setFilters: (_, props) => (filters) => ({
          filters: filters || callFunction(defaultFilters, props),
        }),
        toggleDeleteModal:
          ({ isDeleteModalOpen }) =>
          () => ({
            isDeleteModalOpen: !isDeleteModalOpen,
          }),
        onDeleted:
          ({ ordering }) =>
          (failed) => ({ refreshToken: shortid() }),
        refreshData: () => () => ({ refreshToken: shortid() }),
        onDeleteItem: () => (id) => ({
          isDeleteModalOpen: true,
          selected: { [id]: true },
        }),
      }
    ),
    (BaseComponent) =>
      class extends React.Component {
        renderPagination = (props) => (
          <Pagination
            size="sm"
            limit={this.props.limit}
            offset={this.props.offset}
            onOffsetChanged={this.props.setOffset}
            onLimitChanged={(limit)=>{this.props.setLimit(limit); this.props.setOffset(0)}}
            {...props}
          />
        );

        renderViewingRecords = (props) => (
          <ViewingRecords
            offset={this.props.offset}
            limit={this.props.limit}
            {...props}
          />
        );

        renderTableContainer = (props) => (
          <TableContainer
            items={this.props.data.results}
            ordering={this.props.ordering}
            onOrderingChanged={this.props.setOrdering}
            selected={this.props.selected}
            onSelectedChanged={this.props.setSelected}
            {...props}
          />
        );

        renderXeroTableDataFetch = (props) => {
          const {
            ordering,
            refreshToken,
            limit,
            url,
            filters,
            offset,
            setData,
            setXeroErrors,
          } = this.props;

          return (
            <XeroTableDataFetch
              ordering={ordering}
              refreshToken={refreshToken}
              limit={limit}
              url={url}
              filters={filters}
              offset={offset}
              onDataFetched={setData}
              onDataError={setXeroErrors}
              {...props}
            />
          );
        };

        renderPayrollTableDataFetch = (props) => {
          const {
            ordering,
            refreshToken,
            limit,
            url,
            filters,
            offset,
            setData,
            search,
          } = this.props;

          return (
            <PayrollTableDataFetch
              ordering={ordering}
              refreshToken={refreshToken}
              limit={limit}
              search={search}
              url={url}
              filters={filters}
              offset={offset}
              onDataFetched={setData}
              {...props}
            />
          );
        };

        renderBulkDeleteButton = (props) => {
          const {
            toggleDeleteModal: toggleModal,
            selected,
            url,
            onDeleted,
            isDeleteModalOpen: isModalOpen,
          } = this.props;
          const localProps = {
            toggleModal,
            selected,
            url,
            onDeleted,
            isModalOpen,
          };

          return <BulkDeleteButton {...localProps} {...props} />;
        };

        renderPayrollBulkDeleteButton = (props) => {
          const LocalBulkDeleteButton = this.renderBulkDeleteButton;

          return <LocalBulkDeleteButton client={PayrollClient()} {...props} />;
        };

        renderLoader = () => {
          const { data } = this.props;

          return data.loading ? (
            <tr>
              <td colSpan="100" className="text-center">
                <span className="fa fa-spinner fa-spin mt-2" /> <Trans>Loading...</Trans>
              </td>
            </tr>
          ) : null;
        };

        renderTableSearch = (props) => {
          const { search, setSearch } = this.props;
          return <TableSearch onChange={setSearch} value={search} {...props} />;
        };

        render = () => (
          <BaseComponent
            {...this.props}
            ViewingRecords={this.renderViewingRecords}
            TableContainer={this.renderTableContainer}
            XeroTableDataFetch={this.renderXeroTableDataFetch}
            PayrollTableDataFetch={this.renderPayrollTableDataFetch}
            Pagination={this.renderPagination}
            BulkDeleteButton={this.renderBulkDeleteButton}
            PayrollBulkDeleteButton={this.renderPayrollBulkDeleteButton}
            TableSearch={this.renderTableSearch}
            Loader={this.renderLoader}
          />
        );
      }
  );
